import { additionalPerspectives } from './perspectiveTopics.js';
import { churchQuestionPerspectives } from './perspectiveChurchQuestions.js';
import { doctrineQuestionPerspectives } from './perspectiveDoctrineQuestions.js';
import { eschatologyPerspectives } from './perspectiveEschatology.js';
import { ethicsQuestionPerspectives } from './perspectiveEthicsQuestions.js';
import { ethicsHistoryPerspectives } from './perspectiveEthicsHistory.js';
import { perspectiveFollowups } from './perspectiveFollowups.js';
import { perspectiveExpansion100 } from './perspectiveExpansion100.js';
import { holinessPerspective } from './perspectiveHoliness.js';
import { moralQuestionPerspectives } from './perspectiveMoralQuestions.js';
import { practicePerspectives } from './perspectivePractice.js';
import { salvationQuestionPerspectives } from './perspectiveSalvationQuestions.js';
import { sanctificationPerspective } from './perspectiveSanctification.js';
import { signSeekingPerspectives } from './perspectiveSigns.js';
import { womenMinistryPerspectives } from './perspectiveWomenMinistry.js';
import { holySpiritPerspectives } from './perspectiveHolySpirit.js';
import { logosPerspectives } from './perspectiveLogos.js';

const DEFAULT_PERSPECTIVE_LASTMOD = '2026-08-17';
const PREDICATE_RELATIONSHIP_REVISION_DATE = '2026-10-28';
const PREDICATE_RELATIONSHIP_REVISION_DISPLAY_DATE = '28 October 2026';

const PERSPECTIVE_READING_TITLE_OVERRIDES = {
    'exposes-sin': 'The Law reveals sin but cannot save',
    'guides-covenant-life': 'The Law guides faithful covenant life',
    'counted-verdict': 'God counts believers righteous through Christ',
    'participated-life': 'Righteousness becomes a transformed life',
    'union-as-foundation': 'Union with Christ is the foundation of salvation',
    'union-as-setting': 'Union with Christ surrounds justification',
    'meals-as-boundaries': 'Meals could mark community boundaries',
    'meals-as-gospel': 'Shared meals made the gospel visible',
    'new-people': 'Christ creates one people beyond old boundaries',
    'distinct-within-unity': 'Jewish and Gentile identities can remain distinct',
    'one-gracious-covenant': 'God saves through one covenant of grace',
    'progressive-covenants': 'God’s covenant plan unfolds over time',
    'church-fulfilment': 'God fulfils his promise in one people',
    'distinct-future': 'God still has a future purpose for Israel',
    'one-renewed-people': 'Christ creates one renewed people',
    'distinct-peoples': 'Israel and the church remain distinct within one plan',
    'administration-map': 'One promise unfolds through several covenants',
    'progressive-map': 'Christ fulfils and transforms distinct covenants',
    'covenantal-location': 'Jews and Gentiles need not change identity to belong',
    'vocation-within-change': 'Faithful service remains possible in changing circumstances',
    'genuine-flexibility': 'Paul adapted practice while remaining faithful',
    'pragmatic-accommodation': 'Paul adapted practice to reach different audiences',
    'keep-the-physical-kiss': 'Christians should continue the physical greeting',
    'adapt-the-greeting': 'Christians should preserve the meaning while adapting the greeting',
    'liturgical-sign-of-peace': 'The greeting should remain a liturgical sign of peace',
};

const CANONICAL_PREDICATE_IDS = {
    'moral judgement belongs to continuing formation in grace and holiness': 'sanctification-continuing-moral-formation',
    'holiness forms the whole person for faithful love': 'holiness-whole-person-faithful-love',
    'sanctification is participation in divine life through christ and the spirit': 'sanctification-participation-in-divine-life',
    'entire sanctification is ordered toward perfect love of god and neighbour': 'sanctification-perfect-love-of-god-and-neighbour',
};

const normalizePredicateLabel = (label = '') => label.toLowerCase().replace(/[’']/g, '').replace(/[^a-z0-9]+/g, ' ').trim();

const predicateId = (predicate) => CANONICAL_PREDICATE_IDS[normalizePredicateLabel(predicate.label)] || `predicate-${normalizePredicateLabel(predicate.label).replace(/ /g, '-')}`;

const narrowPredicateTarget = (predicate) => {
    const label = normalizePredicateLabel(predicate.label);
    const target = `${predicate.topicSlug}/${predicate.readingId}`;

    if (target === 'what-is-holiness/christlike-spirit-formed-life') {
        // Map only claims that name a distinctive holiness commitment. Generic
        // ethical vocabulary (love, justice, dignity, care) is shared by many
        // traditions and must not make the specifically Wesleyan perfect-love
        // reading a false high-degree hub.
        if (/(entire sanctification|perfect love of god and (neighbour|neighbor)|perfect love fills|holiness is (shown|expressed) in perfect love)/.test(label)) {
            return { topicSlug: 'what-is-holiness', readingId: 'perfect-love-personal-social' };
        }
        if (/(belong(s|ing) to god|consecrat(ed|ion)|covenant (identity|vocation|faithfulness)|set apart for (god|his purposes))/.test(label)) {
            return { topicSlug: 'what-is-holiness', readingId: 'consecrated-belonging' };
        }
        if (/(participat(e|ion) in (the )?(divine|gods?) life|communion with god|union with god|theosis)/.test(label)) {
            return { topicSlug: 'what-is-holiness', readingId: 'participation-in-divine-life' };
        }
        // Do not treat every generic ethical commitment as a dependency on
        // this particular account of Christlike holiness. Preserve the claim
        // without manufacturing a graph edge when no matching foundation fits.
        return { topicSlug: null, readingId: null, linked: false };
    }

    if (target === 'what-is-sanctification/progressive-holiness') {
        if (/(participat(e|ion) in (the )?(divine|gods?) life|communion with god|union with god|theosis)/.test(label)) {
            return { topicSlug: 'what-is-sanctification', readingId: 'participation-in-divine-life' };
        }
        if (/(entire sanctification|perfect love of god and (neighbour|neighbor)|perfect love fills)/.test(label)) {
            return { topicSlug: 'what-is-sanctification', readingId: 'perfect-love' };
        }
        if (/(definitive|decisive|new realm|justif|transfer)/.test(label)) {
            return { topicSlug: 'what-is-sanctification', readingId: 'definitive-and-progressive' };
        }
    }

    return { topicSlug: predicate.topicSlug, readingId: predicate.readingId };
};

const firstSentence = (text = '') => {
    const match = text.trim().match(/^.*?[.?!](?=\s|$)/);
    return match ? match[0].trim() : text.trim();
};

const truncateAtWord = (text = '', maxLength = 160) => {
    if (text.length <= maxLength) return text;
    const trimmed = text.slice(0, maxLength + 1);
    const boundary = trimmed.lastIndexOf(' ');
    return `${trimmed.slice(0, boundary > 0 ? boundary : maxLength).trim()}...`;
};

const buildPerspectiveSeoDescription = (topic) => {
    const sentence = firstSentence(topic.summary);
    const base = sentence || topic.question;
    return truncateAtWord(base, 158);
};

const normalizePerspectiveTopic = (topic) => {
    const relationshipRevised = (topic.readings || []).some((reading) => (reading.predicates || []).some((predicate) => {
        const current = narrowPredicateTarget(predicate);
        return current.topicSlug !== predicate.topicSlug || current.readingId !== predicate.readingId;
    }));

    return {
        ...topic,
        generation: relationshipRevised && topic.generation
            ? { ...topic.generation, date: PREDICATE_RELATIONSHIP_REVISION_DISPLAY_DATE }
            : topic.generation,
        readings: topic.readings?.map((reading) => {
            const predicates = reading.predicates?.map((predicate) => ({
                ...predicate,
                ...narrowPredicateTarget(predicate),
                canonicalId: predicateId(predicate),
                relationType: 'depends-on',
                foundationLayer: predicate.foundationLayer || 'unclassified',
            }));
            const title = PERSPECTIVE_READING_TITLE_OVERRIDES[reading.id] || reading.title;
            return {
                ...reading,
                title,
                predicates,
                predicateTree: [{
                    id: `argument-${reading.id}`,
                    label: title,
                    thesis: reading.thesis,
                    level: 'argument',
                    children: predicates,
                }],
            };
        }),
        seoDescription: topic.seoDescription || buildPerspectiveSeoDescription(topic),
        lastModified: relationshipRevised ? PREDICATE_RELATIONSHIP_REVISION_DATE : topic.lastModified || DEFAULT_PERSPECTIVE_LASTMOD,
    };
};

const perspectiveTopics = [
    {
        slug: 'what-does-paul-mean-by-works-of-the-law',
        category: 'Paul and the Law',
        question: 'When Paul attacks “works of the Law,” what exactly is he arguing against?',
        shortTitle: 'What Paul means by “works of the Law”',
        summary: 'Paul repeatedly denies that people are justified by “works of the Law,” especially in Galatians and Romans. Interpreters agree that he is defending the sufficiency of Christ, but disagree over whether his immediate target is human achievement, the whole Mosaic Law, Jewish boundary markers, or pressure on Gentiles to become Jews.',
        introduction: 'The phrase appears in arguments about justification, table fellowship, circumcision, Abraham, and the inclusion of Gentiles. Those contexts overlap, which is why the readings below are not always mutually exclusive. Each perspective identifies a different centre of gravity in Paul’s argument.',
        keyTexts: [
            { label: 'Galatians 2:15–21', href: '/read/Gal.2/#Gal.2.15-Gal.2.21' },
            { label: 'Galatians 3:1–14', href: '/read/Gal.3/#Gal.3.1-Gal.3.14' },
            { label: 'Romans 3:19–31', href: '/read/Rom.3/#Rom.3.19-Rom.3.31' },
            { label: 'Philippians 3:2–11', href: '/read/Phil.3/#Phil.3.2-Phil.3.11' },
        ],
        readings: [
            {
                id: 'human-achievement',
                title: 'Obedience cannot earn acceptance with God',
                thesis: 'Paul rejects every attempt to establish right standing before God through obedience or moral achievement.',
                accent: 'navy',
                badges: ['Traditional Protestant', 'Conservative', 'Lutheran', 'Reformed'],
                summary: 'On this reading, “works of the Law” belongs to Paul’s wider contrast between receiving righteousness through faith and trying to secure it through human performance. Circumcision and food practices matter in Galatians, but they exemplify a deeper mistake: treating obedience as a basis for justification rather than receiving God’s promise in Christ.',
                contribution: 'This perspective keeps grace and divine initiative at the centre. It reads Paul’s argument as applying beyond the first-century Jew–Gentile dispute to every religious system in which human achievement becomes the ground of acceptance before God.',
                caution: 'Critics argue that this reading can flatten Paul’s specific concern about Jewish and Gentile fellowship or imply that Second Temple Judaism was simply a religion of merit.',
                supporters: [
                    { name: 'Martin Luther', sourceId: 'luther-galatians' },
                    { name: 'John Calvin', sourceId: 'calvin-romans' },
                ],
                predicates: [
                    {
                        label: 'Justification is chiefly God’s verdict about right standing',
                        topicSlug: 'what-does-paul-mean-by-justification',
                        readingId: 'forensic-verdict',
                        linked: true,
                    },
                    {
                        label: 'The Law reveals sin but cannot supply the righteousness it requires',
                        topicSlug: 'what-can-the-law-do-if-it-cannot-justify',
                        readingId: 'exposes-sin',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'boundary-markers', linked: true },
                    { type: 'overlaps with', target: 'whole-torah', linked: true },
                    {
                        type: 'raises',
                        label: 'What does Paul mean by justification?',
                        topicSlug: 'what-does-paul-mean-by-justification',
                        linked: true,
                    },
                ],
            },
            {
                id: 'boundary-markers',
                title: 'Circumcision and food laws cannot define belonging',
                thesis: 'Paul opposes practices used to make Torah observance the boundary separating Jews from Gentiles in God’s people.',
                accent: 'teal',
                badges: ['New Perspective', 'Progressive', 'Ecumenical'],
                summary: 'This reading emphasizes circumcision, food laws, Sabbath observance, and table fellowship as visible identity markers. Paul is not attacking good works or Judaism as such; he is denying that Gentiles must adopt Jewish covenant badges in order to belong to the family created through Christ.',
                contribution: 'It foregrounds the social and ethnic consequences of justification. “Faith apart from works of the Law” announces one family in Christ and challenges practices that rebuild the dividing wall between Jewish and Gentile believers.',
                caution: 'Critics question whether boundary markers exhaust Paul’s phrase, especially in passages where his language appears to encompass the Law more broadly.',
                supporters: [
                    { name: 'James D. G. Dunn', sourceId: 'dunn-new-perspective' },
                    { name: 'N. T. Wright', sourceId: 'wright-paul' },
                ],
                predicates: [
                    {
                        label: 'Jew–Gentile table fellowship is part of the truth of the gospel',
                        topicSlug: 'why-did-table-fellowship-matter',
                        readingId: 'gospel-enacted',
                        linked: true,
                    },
                    {
                        label: 'Justification identifies the single covenant family',
                        topicSlug: 'what-does-paul-mean-by-justification',
                        readingId: 'covenant-membership',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'contrasts with', target: 'human-achievement', linked: true },
                    { type: 'develops into', target: 'gentile-assimilation', linked: true },
                    {
                        type: 'raises',
                        label: 'Why did table fellowship matter?',
                        topicSlug: 'why-did-table-fellowship-matter',
                        linked: true,
                    },
                ],
            },
            {
                id: 'whole-torah',
                title: 'Paul has the whole Mosaic Law in view',
                thesis: 'Paul has the Mosaic Law as a complete covenantal administration in view, not merely a few identity badges.',
                accent: 'red',
                badges: ['Evangelical', 'Conservative', 'Reformed'],
                summary: 'On this account, Paul’s examples include circumcision and food practices because they were disputed, yet “works of the Law” ultimately means deeds required by the Mosaic Law. The problem is not that the Law is evil, but that it cannot justify sinners and cannot function as the covenant governing the new age inaugurated by Christ.',
                contribution: 'This reading connects Paul’s individual, historical, and covenantal claims. It can affirm the social importance of Jew–Gentile inclusion while maintaining that Paul’s argument reaches beyond boundary markers to the Law’s demands as a whole.',
                caution: 'Critics warn that this approach can understate the immediate ethnic conflict in Galatians 2 and treat Paul’s diverse statements about Torah too uniformly.',
                supporters: [
                    { name: 'Thomas R. Schreiner', sourceId: 'schreiner-galatians' },
                    { name: 'Douglas J. Moo', sourceId: 'moo-galatians' },
                ],
                predicates: [
                    {
                        label: 'Christ inaugurates a new covenantal administration',
                        topicSlug: 'how-does-the-old-covenant-relate-to-the-new',
                        readingId: 'fulfilled-and-transformed',
                        linked: true,
                    },
                    {
                        label: 'Torah observance cannot be the basis of justification',
                        topicSlug: 'what-does-paul-mean-by-justification',
                        readingId: 'forensic-verdict',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'overlaps with', target: 'human-achievement', linked: true },
                    { type: 'qualifies', target: 'boundary-markers', linked: true },
                    {
                        type: 'raises',
                        label: 'How does the old covenant relate to the new?',
                        topicSlug: 'how-does-the-old-covenant-relate-to-the-new',
                        linked: true,
                    },
                ],
            },
            {
                id: 'gentile-assimilation',
                title: 'Gentiles do not need to become Jews',
                thesis: 'Paul resists requiring non-Jews to become Jews; his criticism is directed to Gentile adoption of Torah, not Jewish Torah observance.',
                accent: 'amber',
                badges: ['Paul within Judaism', 'Progressive', 'Jewish-contextual'],
                summary: 'This perspective reads Paul as a Jewish apostle addressing non-Jews at the arrival of Israel’s Messiah. “Works of the Law” becomes dangerous when Gentiles use circumcision and Torah observance to change status and enter God’s people as proselytes. Jewish believers may continue Jewish practice; Gentiles glorify Israel’s God as Gentiles.',
                contribution: 'It sharply distinguishes Paul’s instructions to Gentiles from claims about Judaism generally. The reading also resists using Paul’s polemic to portray Jewish law or Jewish religious life as obsolete, legalistic, or spiritually inferior.',
                caution: 'Critics ask whether this explains passages where Paul seems to make more universal claims about sin, justification, and the Law’s inability to give life.',
                supporters: [
                    { name: 'Mark D. Nanos', sourceId: 'nanos-law-free' },
                    { name: 'Magnus Zetterholm', sourceId: 'paul-within-judaism' },
                    { name: 'Paula Fredriksen', sourceId: 'fredriksen-paul' },
                ],
                predicates: [
                    {
                        label: 'Paul remained a Jewish practitioner of Torah',
                        topicSlug: 'did-paul-remain-torah-observant',
                        readingId: 'torah-observant-jew',
                        linked: true,
                    },
                    {
                        label: 'Jewish and Gentile callings remain distinguishable in Christ',
                        topicSlug: 'does-the-gospel-erase-jewish-and-gentile-identity',
                        readingId: 'distinct-within-unity',
                        linked: true,
                    },
                ],
                relations: [
                    { type: 'extends', target: 'boundary-markers', linked: true },
                    { type: 'challenges', target: 'whole-torah', linked: true },
                    {
                        type: 'raises',
                        label: 'Did Paul remain Torah-observant?',
                        topicSlug: 'did-paul-remain-torah-observant',
                        linked: true,
                    },
                ],
            },
        ],
        sources: [
            {
                id: 'luther-galatians',
                author: 'Martin Luther',
                title: 'Commentary on St. Paul’s Epistle to the Galatians',
                detail: 'Lectures associated with Luther’s mature account of justification and the Law–gospel contrast.',
                href: 'https://www.ccel.org/ccel/luther/galatians.toc.html',
            },
            {
                id: 'calvin-romans',
                author: 'John Calvin',
                title: 'Commentary on Romans',
                detail: 'A classic Reformed account of justification apart from works.',
                href: 'https://ccel.org/ccel/calvin/calcom38/calcom38',
            },
            {
                id: 'dunn-new-perspective',
                author: 'James D. G. Dunn',
                title: 'The New Perspective on Paul',
                detail: 'Collected essays arguing that Paul opposed the Law as a dividing boundary between Jewish and Gentile believers.',
                href: 'https://www.eerdmans.com/9780802845627/the-new-perspective-on-paul/',
            },
            {
                id: 'wright-paul',
                author: 'N. T. Wright',
                title: 'Paul and the Faithfulness of God',
                detail: 'A large-scale account of Paul’s theology, covenant, and the unified people of God.',
                href: 'https://www.fortresspress.com/store/product/9780800626839/Paul-and-the-Faithfulness-of-God',
            },
            {
                id: 'schreiner-galatians',
                author: 'Thomas R. Schreiner',
                title: 'Galatians',
                detail: 'An evangelical commentary arguing that “works of the Law” is broader than boundary markers alone.',
                href: 'https://zondervanacademic.com/products/galatians-1',
            },
            {
                id: 'moo-galatians',
                author: 'Douglas J. Moo',
                title: 'Galatians',
                detail: 'An evangelical commentary on Paul’s argument about faith, justification, and the Mosaic Law.',
                href: 'https://bakeracademic.com/p/galatians-douglas-j-moo/231029',
            },
            {
                id: 'nanos-law-free',
                author: 'Mark D. Nanos',
                title: 'The Myth of the “Law-Free” Paul Standing between Christians and Jews',
                detail: 'An article locating Paul’s instructions within first-century Jewish concerns and Gentile identity.',
                href: 'https://ejournals.bc.edu/index.php/scjr/article/view/1511',
            },
            {
                id: 'paul-within-judaism',
                author: 'Mark D. Nanos and Magnus Zetterholm, editors',
                title: 'Paul Within Judaism',
                detail: 'Essays interpreting Paul and his mission without assuming that he abandoned Jewish life or Torah.',
                href: 'https://books.google.com/books/about/Paul_Within_Judaism.html?id=w6W0BQAAQBAJ',
            },
            {
                id: 'fredriksen-paul',
                author: 'Paula Fredriksen',
                title: 'Paul: The Pagans’ Apostle',
                detail: 'A historical reading of Paul’s mission to gentiles within an apocalyptic Jewish framework.',
                href: 'https://yalebooks.yale.edu/book/9780300240740/paul/',
            },
        ],
        generation: {
            system: 'OpenAI Codex AI assistant',
            date: '14 August 2026',
            status: 'Human review pending',
            note: 'The AI synthesized the draft from the listed biblical and scholarly sources. Associations describe broad affinities, not formal endorsements by every person or tradition represented.',
        },
    },
    ...additionalPerspectives,
    ...churchQuestionPerspectives,
    ...doctrineQuestionPerspectives,
    ...eschatologyPerspectives,
    ...ethicsQuestionPerspectives,
    ...ethicsHistoryPerspectives,
    ...perspectiveFollowups,
    ...perspectiveExpansion100,
    holinessPerspective,
    ...moralQuestionPerspectives,
    ...practicePerspectives,
    ...salvationQuestionPerspectives,
    sanctificationPerspective,
    ...signSeekingPerspectives,
    ...womenMinistryPerspectives,
    ...holySpiritPerspectives,
    ...logosPerspectives,
];

export const perspectives = perspectiveTopics.map(normalizePerspectiveTopic);

export const getPerspectiveBySlug = (slug = '') => perspectives.find((item) => item.slug === slug);

export const getPerspectiveSource = (topic, sourceId) => topic.sources.find((source) => source.id === sourceId);

export const getPerspectivesForBook = (bookCode = '', limit = 3) => {
    if (!bookCode) return [];
    const matches = perspectives.filter((topic) =>
        topic.keyTexts.some((text) => text.href.startsWith(`/read/${bookCode}.`) || text.href.startsWith(`/read/${bookCode}/`))
    );
    return matches.slice(0, limit);
};
